import TurndownService from "turndown";

import { PlaceholderEditorVariant } from "@fronterahealth/frontera-ui-components";
import type { PlaceholderListValue } from "@fronterahealth/frontera-ui-components";

const turndownService = new TurndownService({ headingStyle: "atx", bulletListMarker: "-" });

const HTML_BLOCK_TAG_RE = /<(p|ul|ol|h[1-6]|li|div|table)\b/i;

/** Converts content to Markdown if it appears to be HTML. No-ops on plain text or Markdown. */
export function htmlToMarkdownIfNeeded(content: string): string {
  if (!content || !HTML_BLOCK_TAG_RE.test(content)) return content;
  return turndownService.turndown(content);
}

export { PlaceholderEditorVariant };

/**
 * Horizontal inset for a PlaceholderEditor's content, applied via the editor's
 * `className` (which lands on the outer wrapper, so we reach `.ql-editor` with
 * an arbitrary variant). Quill ships its own *unlayered* padding on
 * `.ql-editor` — ~15px while editing, but the read-only "display only" variant
 * zeroes it (`padding-inline: 0`), so read-only content sits flush against its
 * container. Those library rules are unlayered, which outranks any normal
 * Tailwind utility, so the important modifier is required to win. Gives a
 * consistent inset (matches the `px-3` of sibling inputs) in both variants.
 */
export const EDITOR_CONTENT_INSET_CLASS = "[&_.ql-editor]:px-3!";

interface ExtendedPlaceholderListValue extends PlaceholderListValue {
  key: string;
}

export const PlaceholderValues: ExtendedPlaceholderListValue[] = [
  // Client Information
  { id: 1, value: "Client Full Name", icon: "user", key: "clientFullName" },
  { id: 2, value: "Client First Name", icon: "user", key: "clientFirstName" },
  { id: 3, value: "Client Last Name", icon: "user", key: "clientLastName" },
  { id: 4, value: "Date of Birth", icon: "calendar", key: "dateOfBirth" },
  { id: 5, value: "Age", icon: "calendar", key: "age" },
  { id: 6, value: "Gender", icon: "user", key: "gender" },
  { id: 7, value: "Address", icon: "map-pin", key: "address" },
  { id: 29, value: "EHR Client ID", icon: "user", key: "learnerExternalId" },

  // Primary Guardian/Caregiver Information
  { id: 8, value: "Guardian Full Name", icon: "users", key: "guardianFullName" },
  { id: 9, value: "Guardian First Name", icon: "users", key: "guardianFirstName" },
  { id: 10, value: "Guardian Last Name", icon: "users", key: "guardianLastName" },
  { id: 11, value: "Guardian Phone", icon: "phone", key: "guardianPhone" },

  // Secondary Guardian/Caregiver Information
  { id: 21, value: "Guardian 2 Full Name", icon: "users", key: "guardian2FullName" },
  { id: 22, value: "Guardian 2 First Name", icon: "users", key: "guardian2FirstName" },
  { id: 23, value: "Guardian 2 Last Name", icon: "users", key: "guardian2LastName" },
  { id: 24, value: "Guardian 2 Phone", icon: "phone", key: "guardian2Phone" },

  // Both Guardians/Caregivers, joined with ", "
  { id: 30, value: "Caregiver Names", icon: "users", key: "caregiverNames" },

  // Report Information
  { id: 12, value: "Email", icon: "mail", key: "email" },
  { id: 13, value: "Date of Report", icon: "calendar", key: "dateOfReport" },
  { id: 14, value: "Signed By", icon: "pen-tool", key: "signedBy" },
  { id: 15, value: "Professional Title", icon: "briefcase", key: "professionalTitle" },
  { id: 16, value: "Practitioner Email", icon: "mail", key: "practitionerEmail" },
  { id: 17, value: "Report Writer", icon: "edit", key: "reportWriter" },
  { id: 18, value: "Date and Time of Assessment", icon: "calendar", key: "dateAndTimeOfAssessment" },
  { id: 19, value: "License Number", icon: "user", key: "signedByLicenseNumber" },

  // Organization Information
  { id: 20, value: "Organization Name", icon: "briefcase", key: "organizationName" },

  // Insurance Information
  { id: 25, value: "Insurer", icon: "briefcase", key: "insurer" },
  { id: 26, value: "Insurance ID", icon: "briefcase", key: "insuranceId" },
  { id: 27, value: "Authorization Start", icon: "calendar", key: "authorizationStart" },
  { id: 28, value: "Authorization End", icon: "calendar", key: "authorizationEnd" },
];

const PLACEHOLDER_DISPLAY_TO_KEY = new Map<string, string>(PlaceholderValues.map((p) => [p.value, p.key]));
const PLACEHOLDER_KEY_TO_DISPLAY = new Map<string, string>(PlaceholderValues.map((p) => [p.key, p.value]));

/**
 * Backend aliases that resolve to the exact same value as their canonical
 * counterpart (api-django's report_placeholder_service.py
 * `_populate_client_data`/`_set_guardian_data` set `clientName`/
 * `guardian(2)Name` and `clientFullName`/`guardian(2)FullName` from the
 * identical computed full name). The `Name` variants aren't in
 * `PlaceholderValues` above, so without this they never convert to a
 * display chip -- they just sit as raw `{clientName}`-style text in the
 * editor even though report generation substitutes them correctly.
 * One-directional: recognized for DISPLAY only, so existing content using
 * an alias renders properly, but selecting the placeholder from the
 * picker always writes the canonical key below.
 */
const PLACEHOLDER_KEY_ALIASES: Record<string, string> = {
  clientName: "clientFullName",
  guardianName: "guardianFullName",
  guardian2Name: "guardian2FullName",
};
for (const [alias, canonicalKey] of Object.entries(PLACEHOLDER_KEY_ALIASES)) {
  const display = PLACEHOLDER_KEY_TO_DISPLAY.get(canonicalKey);
  if (display) {
    PLACEHOLDER_KEY_TO_DISPLAY.set(alias, display);
  }
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Turndown (used internally by PlaceholderEditor) escapes underscores to `\_`
 * to prevent accidental Markdown emphasis. This breaks custom dynamic
 * placeholders like `{Selected_Treatment_Option}` -> `{Selected\_Treatment\_Option}`.
 * Un-escape backslash-prefixed characters inside placeholder-shaped tokens
 * before saving. Only tokens whose inner text looks like a placeholder name
 * (word chars, spaces, dots, hyphens, plus Turndown escapes) are touched —
 * this function also runs on table/choice field `content`, which is JSON
 * whose `{...}` spans (quotes, colons, escaped backslashes) must pass
 * through byte-for-byte or the next JSON.parse fails.
 */
function unescapePlaceholderContent(text: string): string {
  return text.replace(
    /\{((?:[\w .-]|\\[_*`[\]\\])+)\}/g,
    (_, inner: string) => `{${inner.replace(/\\([_*`[\]\\])/g, "$1")}}`,
  );
}

export function convertDisplayPlaceholdersToKeys(text: string): string {
  if (!text) return text;
  let result = text.replace(/\uFEFF/g, "");
  result = unescapePlaceholderContent(result);
  PLACEHOLDER_DISPLAY_TO_KEY.forEach((key, displayName) => {
    result = result.replace(new RegExp(escapeRegExp(`{${displayName}}`), "gi"), `{${key}}`);
  });
  return result.replace(/\uFEFF/g, "");
}

export function convertKeyPlaceholdersToDisplay(text: string): string {
  if (!text) return text;
  let result = text;
  PLACEHOLDER_KEY_TO_DISPLAY.forEach((displayName, key) => {
    result = result.replace(new RegExp(escapeRegExp(`{${key}}`), "g"), `{${displayName}}`);
  });
  return result;
}
